import React, { useState, useEffect } from 'react';
import { Authenticated, Unauthenticated, useQuery, useMutation } from "convex/react";
import { api } from "../convex/_generated/api";
import { SignInForm } from "./SignInForm";
import { SignOutButton } from "./SignOutButton";
import { Toaster, toast } from "sonner";
import Header from './components/Header';
import PhotoGrid from './components/PhotoGrid';
import MusicPlayer from './components/MusicPlayer';
import UploadModal from './components/UploadModal';
import NotesGrid from './components/NotesGrid';
import { Photo, Song } from './types';

function App() {
  // 从Convex获取数据
  const photos = useQuery(api.photos.listPublicPhotos) || [];
  const songs = useQuery(api.music.listPublicSongs) || [];
  const loggedInUser = useQuery(api.auth.loggedInUser);
  
  // 上传mutations
  const uploadPhoto = useMutation(api.photos.uploadPhoto);
  const uploadMusic = useMutation(api.music.uploadMusic);
  const generatePhotoUploadUrl = useMutation(api.photos.generateUploadUrl);
  const generateMusicUploadUrl = useMutation(api.music.generateUploadUrl);
  
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [currentView, setCurrentView] = useState<'photos' | 'notes'>('photos');
  
  // Audio State
  const [currentSong, setCurrentSong] = useState<Song | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // 转换Convex数据格式为组件需要的格式
  const convertedPhotos: Photo[] = photos.map(photo => ({
    id: photo._id,
    url: photo.imageUrl || '',
    caption: photo.description || photo.title || '',
    date: new Date(photo._creationTime).toLocaleDateString('zh-CN')
  }));

  const convertedSongs: Song[] = songs.map(song => ({
    id: song._id,
    title: song.title,
    artist: song.artist || '陈楚生',
    url: song.audioUrl || '',
    coverUrl: song.coverUrl || `https://picsum.photos/200/200?random=${song._id}`
  }));

  // Initialize first song but don't play automatically to be polite
  useEffect(() => {
    if (convertedSongs.length > 0 && !currentSong) {
      setCurrentSong(convertedSongs[0]);
    }
  }, [convertedSongs, currentSong]);

  const handleUploadPhoto = async (file: File, caption: string) => {
    try {
      // 1. 生成上传URL
      const uploadUrl = await generatePhotoUploadUrl();
      
      // 2. 上传文件到Convex storage
      const result = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": file.type },
        body: file,
      });
      
      if (!result.ok) {
        throw new Error("文件上传失败");
      }
      
      const { storageId } = await result.json();
      
      // 3. 保存照片信息到数据库
      await uploadPhoto({
        title: "新照片",
        description: caption,
        imageId: storageId,
        tags: [],
        isPublic: true,
      });
      
      setIsUploadModalOpen(false);
      toast.success('照片上传成功！');
      console.log('照片上传成功！');
    } catch (error) {
      console.error('上传照片失败:', error);
      toast.error('照片上传失败，请重试');
      throw error; // 重新抛出错误，让UploadModal处理
    }
  };

  const handleUploadMusic = async (file: File, title: string, artist: string) => {
    try {
      // 1. 生成上传URL
      const uploadUrl = await generateMusicUploadUrl();
      
      // 2. 上传文件到Convex storage
      const result = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": file.type },
        body: file,
      });
      
      if (!result.ok) {
        throw new Error("文件上传失败");
      }
      
      const { storageId } = await result.json();
      
      // 3. 保存音乐信息到数据库
      await uploadMusic({
        title,
        artist,
        audioId: storageId,
        isPublic: true,
      });
      
      setIsUploadModalOpen(false);
      toast.success('音乐上传成功！');
      console.log('音乐上传成功！');
    } catch (error) {
      console.error('上传音乐失败:', error);
      toast.error('音乐上传失败，请重试');
      throw error; // 重新抛出错误，让UploadModal处理
    }
  };

  // Music Controls
  const togglePlayPause = () => setIsPlaying(!isPlaying);
  
  const playNext = () => {
    if (!currentSong) return;
    const idx = convertedSongs.findIndex(s => s.id === currentSong.id);
    const nextIdx = (idx + 1) % convertedSongs.length;
    setCurrentSong(convertedSongs[nextIdx]);
    setIsPlaying(true);
  };

  const playPrev = () => {
    if (!currentSong) return;
    const idx = convertedSongs.findIndex(s => s.id === currentSong.id);
    const prevIdx = (idx - 1 + convertedSongs.length) % convertedSongs.length;
    setCurrentSong(convertedSongs[prevIdx]);
    setIsPlaying(true);
  };

  const selectSong = (song: Song) => {
    setCurrentSong(song);
    setIsPlaying(true);
  };

  if (loggedInUser === undefined) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-stone-300 border-t-stone-800"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 text-stone-800 font-sans selection:bg-emerald-200 selection:text-emerald-900">
      <Unauthenticated>
        <div className="min-h-screen flex items-center justify-center p-3 sm:p-4">
          <div className="max-w-sm sm:max-w-lg mx-auto bg-white rounded-2xl sm:rounded-3xl p-8 sm:p-12 shadow-sm border border-stone-200/60 text-center w-full">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-6 sm:mb-8 bg-stone-800 rounded-full flex items-center justify-center shadow-lg">
              <span className="text-2xl sm:text-3xl">🎵</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl font-serif text-stone-800 mb-4 sm:mb-6 leading-tight">
              欢迎来到<br />楚生印象
            </h2>
            
            <p className="text-stone-600 mb-3 sm:mb-4 text-base sm:text-lg leading-relaxed">
              在这里，每一首歌都是一个故事
            </p>
            <p className="text-stone-500 mb-8 sm:mb-12 font-medium text-sm sm:text-base">
              登录后可以浏览更多精彩内容，管理员可以上传照片和音乐
            </p>
            
            <SignInForm />
          </div>
        </div>
      </Unauthenticated>

      <Authenticated>
        <Header 
          onUploadClick={() => setIsUploadModalOpen(true)} 
          currentView={currentView}
          onViewChange={setCurrentView}
        />

        <main className="max-w-7xl mx-auto py-4 sm:py-8">
          <div className="px-3 sm:px-6 lg:px-8 mb-6 sm:mb-8">
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-stone-200/60 text-center mb-6 sm:mb-10">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-serif text-stone-800 mb-3 sm:mb-4">
                {currentView === 'photos' ? '"听见花开的声音"' : '"记录心中的想法"'}
              </h2>
              <p className="text-stone-500 max-w-2xl mx-auto leading-relaxed text-sm sm:text-base">
                {currentView === 'photos' 
                  ? '这里是一个关于简单与旋律的空间。我们收集那些伴随着吉他和真诚歌声的瞬间，让时间在此刻停留。'
                  : '在这里记录你的灵感、想法和感悟。每一个便签都是心灵的片段，值得被珍藏和分享。'
                }
              </p>
            </div>
          </div>

          {currentView === 'photos' ? (
            <PhotoGrid photos={convertedPhotos} />
          ) : (
            <NotesGrid />
          )}
        </main>

        <MusicPlayer 
          currentSong={currentSong}
          isPlaying={isPlaying}
          onPlayPause={togglePlayPause}
          onNext={playNext}
          onPrev={playPrev}
          playlist={convertedSongs}
          onSelectSong={selectSong}
        />

        <UploadModal 
          isOpen={isUploadModalOpen} 
          onClose={() => setIsUploadModalOpen(false)}
          onUploadPhoto={handleUploadPhoto}
          onUploadMusic={handleUploadMusic}
        />
      </Authenticated>

      <Toaster 
        theme="light"
        toastOptions={{
          style: {
            background: 'white',
            border: '1px solid #e7e5e4',
            color: '#1c1917',
          },
        }}
      />
    </div>
  );
}

export default App;